import { take, put } from 'redux-saga/effects'
import { prop } from 'ramda'
import {
  BATCH_IMPORT_COMPLETE,
  SAVE_REFERENCE_PARAMETER_NOTES,
  RP_ACTIVATION_COMPLETE,
  RP_SP_ACTIVATION_FAILURE,
  SAVE_REFERENCE_PARAM,
  DUPLICATE_REFERENCE_PARAM,
  DUPLICATE_REFERENCE_PARAM_FAIL,
  SAVE_REFERENCE_PARAM_FAILURE,
  UPDATE_RP_STATUS_FAILURE,
  LINK_SUPPORT_PARAM_TO_CURRENT_REFERENCE_PARAM,
} from 'actions/referenceParameter'
import {
  GET_VALIDATE_PTP,
  UPDATE_EXM,
  ADD_FEATURE_VALIDATION_ERROR,
} from 'actions/truck'
import {
  FAILURE_SAVE_DASH_CONFIG,
  FAILURE_SAVE_LAYOUT_LOCATION,
  DASH_CONFIG_INVALID,
} from 'actions/dash'
import {
  SAVE_SUPPORT_PARAM,
  DUPLICATE_SUPPORT_PARAM,
  DUPLICATE_SUPPORT_PARAM_FAIL,
  ACTIVATE_SUPPORT_PARAM,
  SUPPORT_PARAM_ACTIVATION_ERROR,
  SP_ACTIVATED,
} from 'actions/supportParameter'
import {
  REMOVE_CIRCUIT_FAIL,
  REMOVE_CIRCUIT_SUCCESS,
  MOVE_CIRCUIT_FAIL,
  MOVE_CIRCUIT_SUCCESS,
  CIRCUIT_BUNDLES_NOT_RECEIVED,
  CIRCUIT_DETAILS_NOT_RECEIVED,
  CIRCUIT_REVISIONS_NOT_RECEIVED,
  BUNDLE_DETAILS_NOT_RECEIVED,
  BUNDLE_REVISIONS_NOT_RECEIVED,
} from 'actions/ctp'
import { NOTIFICATION_ERROR, NOTIFICATION_SUCCESS } from 'utils/constants.js'
import { ECP_MODULE_UPDATED } from 'actions/ecp'
import {
  UPDATE_USER_SETTING_SUCCESS,
  UPDATE_USER_SETTING_FAIL,
  UPDATE_USER_SETTINGS_SUCCESS,
  UPDATE_USER_SETTINGS_FAIL,
} from 'actions/user'
import { createSystemNotification } from 'actions/application'
import {
  UPDATE_FRAGMENTS_SUCCESS,
  UPDATE_FRAGMENTS_FAIL,
} from 'actions/powerTakeOff'
import {
  SAVE_RELEASE_NOTE_SUCCESS,
  SAVE_RELEASE_NOTE_FAILURE,
} from 'actions/releaseNotes'
import { HARNESSES_FAILURE } from 'actions/feature/harnesses'
import {
  ACTIVATE_COLLECTION_SUCCESS,
  ACTIVATE_COLLECTION_FAILURE,
  OBSOLETE_COLLECTION_SUCCESS,
  OBSOLETE_COLLECTION_FAILURE,
} from 'actions/collection'
import strings from 'utils/strings'
import { RPDuplicationErrorCodes } from '../../i18n/displayConstants/errorCodes'

const toastDictionary = {
  [UPDATE_EXM]: updateExm,
  [BATCH_IMPORT_COMPLETE]: batchImportComplete,
  [LINK_SUPPORT_PARAM_TO_CURRENT_REFERENCE_PARAM]: linkSPToCurrentRP,
  [DUPLICATE_REFERENCE_PARAM]: duplicateReferenceParameter,
  [DUPLICATE_REFERENCE_PARAM_FAIL]: duplicateReferenceParameterFail,
  [SAVE_REFERENCE_PARAM]: saveReferenceParameter,
  [SAVE_REFERENCE_PARAM_FAILURE]: saveReferenceParameterFailure,
  [GET_VALIDATE_PTP]: getValidatePTP,
  [FAILURE_SAVE_DASH_CONFIG]: vehicleDashConfigReceivedOnSaveError,
  [FAILURE_SAVE_LAYOUT_LOCATION]: layoutReceivedOnSaveError,
  [DASH_CONFIG_INVALID]: vehicleDashConfigInvalid,
  [SAVE_REFERENCE_PARAMETER_NOTES]: saveRPNotes,
  [SAVE_SUPPORT_PARAM]: saveSupportParameter,
  [DUPLICATE_SUPPORT_PARAM]: duplicateSupportParameter,
  [DUPLICATE_SUPPORT_PARAM_FAIL]: duplicateSupportParameterFail,
  [ACTIVATE_SUPPORT_PARAM]: activateSupportParameter,
  [MOVE_CIRCUIT_SUCCESS]: moveCircuit,
  [MOVE_CIRCUIT_FAIL]: moveCircuitFail,
  [REMOVE_CIRCUIT_SUCCESS]: removeCircuit,
  [REMOVE_CIRCUIT_FAIL]: removeCircuitFail,
  [RP_ACTIVATION_COMPLETE]: activatedReferenceParameter,
  [RP_SP_ACTIVATION_FAILURE]: activationFailureSPStatusError,
  [SUPPORT_PARAM_ACTIVATION_ERROR]: activationFailureSPDatabaseError,
  [SP_ACTIVATED]: activatedSupportParameter,
  [ECP_MODULE_UPDATED]: ecpModuleUpdated,
  [CIRCUIT_BUNDLES_NOT_RECEIVED]: circuitBundlesNotFound,
  [CIRCUIT_REVISIONS_NOT_RECEIVED]: circuitRevisionsNotFound,
  [CIRCUIT_DETAILS_NOT_RECEIVED]: circuitDetailsNotFound,
  [BUNDLE_DETAILS_NOT_RECEIVED]: bundleDetailsNotFound,
  [BUNDLE_REVISIONS_NOT_RECEIVED]: bundleRevisionsNotFound,
  [UPDATE_USER_SETTING_SUCCESS]: handleSettingSaved,
  [UPDATE_USER_SETTINGS_SUCCESS]: handleAllSettingsSaved,
  [UPDATE_USER_SETTING_FAIL]: handleSettingSavedFail,
  [UPDATE_USER_SETTINGS_FAIL]: handleAllSettingsSavedFail,
  [UPDATE_FRAGMENTS_SUCCESS]: handleFragmentsSaved,
  [UPDATE_FRAGMENTS_FAIL]: handleFragmentsSavedFail,
  [SAVE_RELEASE_NOTE_SUCCESS]: handleSaveReleaseNoteSuccess,
  [SAVE_RELEASE_NOTE_FAILURE]: handleSaveReleaseNoteFailure,
  [HARNESSES_FAILURE]: handleGetHarnessesFailure,
  [ADD_FEATURE_VALIDATION_ERROR]: handleAddFeatureValidationFailure,
  [UPDATE_RP_STATUS_FAILURE]: handleUpdateRPStatusFailure,
  // Duplicate collection notifications dispatched directly from CollectionLifeCycle component
  [ACTIVATE_COLLECTION_SUCCESS]: activateCollectionSuccess,
  [ACTIVATE_COLLECTION_FAILURE]: activateCollectionFail,
  [OBSOLETE_COLLECTION_SUCCESS]: obsoleteCollectionSuccess,
  [OBSOLETE_COLLECTION_FAILURE]: obsoleteCollectionFail,
}
// For toast notifications, pass persistent prop as false.
const getToastInfoFromAction = (action) => toastDictionary[action.type](action)

export default function* watchForToastNotifications() {
  while (true) {
    const action = yield take(Object.keys(toastDictionary))
    const toastText = getToastInfoFromAction(action)
    if (toastText) {
      yield put(createSystemNotification(toastText))
    }
  }
}

function handleSettingSaved({ settings }) {
  const { settingName } = settings
  if (settingName) {
    return { notificationMessage: strings.setting_saved(settingName) }
  }
  return { notificationMessage: strings.setting_saved() }
}

function handleAllSettingsSaved() {
  return { notificationMessage: strings.all_settings_saved() }
}

function handleSettingSavedFail({ settings }) {
  const { settingName } = settings
  if (settingName) {
    return {
      notificationMessage: strings.setting_save_error(settingName),
      notificationType: NOTIFICATION_ERROR,
    }
  }
  return {
    notificationMessage: strings.setting_save_error(),
    notificationType: NOTIFICATION_ERROR,
  }
}

function handleAllSettingsSavedFail() {
  return {
    notificationMessage: strings.all_settings_save_error,
    notificationType: NOTIFICATION_ERROR,
  }
}

function updateExm({ moduleName, size }) {
  return {
    notificationMessage: strings.exm_upgraded_on_vehicle(moduleName, size),
  }
}

function linkSPToCurrentRP() {
  return { notificationMessage: strings.support_parameter_added }
}

function circuitRevisionsNotFound() {
  return {
    notificationMessage: strings.circuit_revisions_not_found,
    notificationType: NOTIFICATION_ERROR,
  }
}

function bundleDetailsNotFound() {
  return {
    notificationMessage: strings.bundle_details_not_found,
    notificationType: NOTIFICATION_ERROR,
  }
}

function bundleRevisionsNotFound() {
  return {
    notificationMessage: strings.bundle_revisions_not_found,
    notificationType: NOTIFICATION_ERROR,
  }
}

function circuitDetailsNotFound() {
  return {
    notificationMessage: strings.circuit_details_not_found,
    notificationType: NOTIFICATION_ERROR,
  }
}

function circuitBundlesNotFound() {
  return {
    notificationMessage: strings.circuit_bundles_not_found,
    notificationType: NOTIFICATION_ERROR,
  }
}

function duplicateReferenceParameter() {
  return { notificationMessage: strings.ref_param_duplicated }
}

function duplicateReferenceParameterFail({ code, description }) {
  return {
    notificationMessage: RPDuplicationErrorCodes[code],
    notificationType: NOTIFICATION_ERROR,
  }
}

function saveReferenceParameter() {
  return { notificationMessage: strings.reference_parameter_saved }
}

function saveReferenceParameterFailure() {
  return { notificationMessage: strings.reference_parameter_save_failed }
}

function removeCircuitFail() {
  return {
    notificationMessage: strings.remove_circuit_failed,
    notificationType: NOTIFICATION_ERROR,
  }
}

function removeCircuit({ circuitNumber }) {
  return { notificationMessage: strings.removal_complete(circuitNumber) }
}
function moveCircuitFail() {
  return {
    notificationMessage: strings.move_circuit_failed,
    notificationType: NOTIFICATION_ERROR,
  }
}

function moveCircuit({ circuitNumber }) {
  return { notificationMessage: strings.move_circuit_complete(circuitNumber) }
}

function activateSupportParameter() {
  return { notificationMessage: strings.parameter_status_updated }
}

function duplicateSupportParameter() {
  return {
    notificationMessage: strings.support_parameter_added_to_reference_parameter,
  }
}
function duplicateSupportParameterFail({ code }) {
  return code === '401'
    ? {
        notificationMessage: strings.duplicate_sp_auth_fail,
        notificationType: NOTIFICATION_ERROR,
      }
    : null
}

function activateCollectionSuccess() {
  return {
    notificationMessage: strings.collection_activated_success,
    persistent: true,
    closable: true,
  }
}

function activateCollectionFail() {
  return {
    notificationMessage: strings.collection_api_error,
    notificationType: NOTIFICATION_ERROR,
    persistent: true,
    closable: true,
  }
}

function obsoleteCollectionSuccess() {
  return {
    notificationMessage: strings.collection_obsoleted_success,
    persistent: true,
    closable: true,
  }
}

function obsoleteCollectionFail() {
  return {
    notificationMessage: strings.collection_api_error,
    notificationType: NOTIFICATION_ERROR,
    persistent: true,
    closable: true,
  }
}

function saveSupportParameter() {
  return { notificationMessage: strings.support_param_saved }
}

function activationFailureSPStatusError() {
  return {
    notificationMessage: strings.activating_sp_and_rp_activation_error,
    notificationType: NOTIFICATION_ERROR,
    persistent: false,
    async: false,
    closable: true,
  }
}

function activationFailureSPDatabaseError() {
  return {
    notificationMessage: strings.database_error_rp_sp_activation,
    notificationType: NOTIFICATION_ERROR,
    persistent: false,
    async: false,
    closable: true,
  }
}

function activatedReferenceParameter() {
  return { notificationMessage: strings.activated_reference_parameter }
}

function activatedSupportParameter() {
  return { notificationMessage: strings.activated_support_parameter }
}

function saveRPNotes() {
  return { notificationMessage: strings.reference_parameter_notes_saved }
}
function batchImportComplete() {
  return { notificationMessage: strings.batch_import_success_toast }
}

function getValidatePTP() {
  return { notificationMessage: strings.refresh_complete }
}

function vehicleDashConfigReceivedOnSaveError() {
  return {
    notificationMessage:
      strings.something_went_wrong_while_trying_to_save_your_changes,
    notificationType: NOTIFICATION_ERROR,
  }
}

function layoutReceivedOnSaveError() {
  return {
    notificationMessage:
      strings.something_went_wrong_while_trying_to_update_a_location,
    notificationType: NOTIFICATION_ERROR,
  }
}

function vehicleDashConfigInvalid(action) {
  const invalids = action.invalidLocations
    .reduce((acc, curr) => acc.concat(curr.panelNumber), [])
    .join(',')
  return {
    notificationMessage: strings.dash_configuration_error(invalids),
    notificationType: NOTIFICATION_ERROR,
    persistent: false,
    async: false,
    closable: true,
  }
}

function ecpModuleUpdated() {
  return {
    notificationMessage: strings.ecp_update_notification,
    notificationType: NOTIFICATION_SUCCESS,
    persistent: true,
    async: false,
    closable: true,
  }
}

function handleFragmentsSaved() {
  return { notificationMessage: strings.pto_updated_toast }
}

function handleFragmentsSavedFail() {
  return {
    notificationMessage: strings.pto_updated_toast_fail,
    notificationType: NOTIFICATION_ERROR,
  }
}

function handleSaveReleaseNoteSuccess() {
  return { notificationMessage: strings.release_note_saved }
}

function handleSaveReleaseNoteFailure(action) {
  const { error } = action
  if (error) {
    return [prop('description', error), NOTIFICATION_ERROR]
  }
  return {
    notificationMessage: strings.failed_to_save_release_note,
    notificationType: NOTIFICATION_ERROR,
  }
}

function handleGetHarnessesFailure() {
  return {
    notificationMessage: strings.get_harnesses_failure,
    notificationType: NOTIFICATION_ERROR,
  }
}

function handleAddFeatureValidationFailure() {
  return {
    notificationMessage: strings.an_error_has_occurred,
    notificationType: NOTIFICATION_ERROR,
    persistent: false,
  }
}

function handleUpdateRPStatusFailure() {
  return {
    notificationMessage: strings.reference_parameter_status_update_failed,
    notificationType: NOTIFICATION_ERROR,
  }
}
